import React, { useState, useEffect, useRef } from "react";
import "../App.css";
import { getTokenInternal } from "./useToken";
import { useNavigate } from "react-router";
import Nav from "./Navigation.js";

const CreateItemForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    types: "",
    event: "",
    maps: "",
    maps_name: "",
    url: "",
    games_id: "",
    acquisition_method_id: "",
    is_active: false,
    is_new: false,
    private_testing: false,
  });

  const [token, setToken] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [mapsNameOptions, setMapsNameOptions] = useState([]);
  const [gamesNameOptions, setGamesNameOptions] = useState([]);
  const [mapsExclusiveOptions, setMapsExclusiveOptions] = useState([]);
  const [acquisitionMethodOptions, setAcquisitionMethodOptions] = useState([]);
  const [unlockRequirementOptions, setUnlockRequirementOptions] = useState([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState(new Set());
  const [itemCreated, setItemCreated] = useState(false);
  const navigate = useNavigate();
  const prevGamesId = useRef(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const fetchedToken = await getTokenInternal();
        setToken(fetchedToken);
      } catch (error) {
        console.error("Error fetching token:", error);
      }
    };

    const fetchDropdownOptions = async () => {
      try {
        const [
          typesResponse,
          eventsResponse,
          mapsResponse,
          mapsNameResponse,
          gamesResponse,
          acquisitionResponse,
        ] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/types`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/events`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/maps`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/maps_name`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/games`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods`),
        ]);

        setTypeOptions(await typesResponse.json());
        setEventOptions(await eventsResponse.json());
        setMapsExclusiveOptions(await mapsResponse.json());
        setMapsNameOptions(await mapsNameResponse.json());
        setGamesNameOptions(await gamesResponse.json());
        setAcquisitionMethodOptions(await acquisitionResponse.json());
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };

    fetchToken();
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (!formData.games_id || formData.games_id === prevGamesId.current) return;
    prevGamesId.current = formData.games_id;
    setSelectedRequirementIds(new Set());
    fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements?games_id=${formData.games_id}`)
      .then(r => r.ok ? r.json() : [])
      .then(setUnlockRequirementOptions);
  }, [formData.games_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const toggleRequirement = (reqId) => {
    setSelectedRequirementIds(prev => {
      const next = new Set(prev);
      if (next.has(reqId)) next.delete(reqId); else next.add(reqId);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!token) {
        console.error("Token is not available.");
        return;
      }

      const payload = {
        ...formData,
        games_id: formData.games_id ? parseInt(formData.games_id) : null,
        acquisition_method_id: formData.acquisition_method_id
          ? parseInt(formData.acquisition_method_id)
          : null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const newItem = await response.json();
        if (selectedRequirementIds.size > 0) {
          await Promise.all(
            [...selectedRequirementIds].map(id => fetch(
              `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}/link-item/${newItem.id}`,
              { method: "POST", headers: { Authorization: `Bearer ${token.access_token}` } }
            ))
          );
        }
        setItemCreated(true);
        setSelectedRequirementIds(new Set());
        setTimeout(() => setItemCreated(false), 5000);
      } else {
        console.error("Failed to create item:", response.status);
        setItemCreated(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setItemCreated(false);
    }
  };

  useEffect(() => {
    if (
      token &&
      !["owner", "management", "helper"].includes(token.account.role)
    ) {
      navigate("/");
    }
  }, [token, navigate]);

  return (
    <div className="text-white">
      <Nav />
      {itemCreated && (
        <p className="text-green-500 text-center">Item created!</p>
      )}
      <form onSubmit={handleSubmit} className="max-w-md mx-auto my-8">
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Game Name:
            <select
              name="games_id"
              value={formData.games_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="" disabled>Select a game</option>
              {gamesNameOptions.map((game) => (
                <option key={game.id} value={game.id}>{game.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">Name:</label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="p-2 flex-1 border text-black rounded-md"
            />
            {formData.name && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, name: "" }))}
                className="text-gray-400 hover:text-white text-lg font-bold px-1"
                title="Clear name"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Category:
            <select
              name="types"
              value={formData.types}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="" disabled>Select a type</option>
              {typeOptions.map((type) => (
                <option key={type.id} value={type.name}>{type.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Event:
            <select
              name="event"
              value={formData.event}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="" disabled>Select an event</option>
              {eventOptions.map((event) => (
                <option key={event.id} value={event.name}>{event.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Stamp type or Maps Exclusive:
            <select
              name="maps"
              value={formData.maps}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="" disabled>Select a maps exclusive</option>
              {mapsExclusiveOptions.map((m) => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Maps Name:
            <select
              name="maps_name"
              value={formData.maps_name}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="" disabled>Select a maps name</option>
              {mapsNameOptions.map((mn) => (
                <option key={mn.id} value={mn.name}>{mn.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">
            Acquisition Method:
            <select
              name="acquisition_method_id"
              value={formData.acquisition_method_id}
              onChange={handleChange}
              className="mt-1 p-2 w-full border text-black rounded-md"
            >
              <option value="">None / unspecified</option>
              {acquisitionMethodOptions.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
        </div>
        {unlockRequirementOptions.length > 0 && (
          <div className="mb-4">
            <label className="block text-white text-sm font-bold mb-2">Unlock Requirements:</label>
            <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2 bg-gray-700">
              {unlockRequirementOptions.map(req => (
                <label key={req.id} className="flex items-center gap-2 cursor-pointer text-sm font-normal text-white">
                  <input
                    type="checkbox"
                    checked={selectedRequirementIds.has(req.id)}
                    onChange={() => toggleRequirement(req.id)}
                    className="w-4 h-4"
                  />
                  {req.name}{req.platform ? ` (${req.platform})` : ""}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="mb-4">
          <label className="block text-white text-sm font-bold mb-2">URL:</label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              name="url"
              value={formData.url}
              onChange={handleChange}
              className="p-2 flex-1 border text-black rounded-md"
            />
            {formData.url && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, url: "" }))}
                className="text-gray-400 hover:text-white text-lg font-bold px-1"
                title="Clear URL"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="mb-4 flex gap-6">
          <label className="flex items-center gap-2 text-white text-sm font-bold cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4"
            />
            Active (visible to players)
          </label>
          <label className="flex items-center gap-2 text-white text-sm font-bold cursor-pointer">
            <input
              type="checkbox"
              name="is_new"
              checked={formData.is_new}
              onChange={handleChange}
              className="w-4 h-4"
            />
            New this season
          </label>
          <label className="flex items-center gap-2 text-white text-sm font-bold cursor-pointer">
            <input
              type="checkbox"
              name="private_testing"
              checked={formData.private_testing}
              onChange={handleChange}
              className="w-4 h-4"
            />
            Private testing only
          </label>
        </div>

        <div className="text-center">
          <button
            type="submit"
            className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-700"
          >
            Create Item
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateItemForm;
